<template>
  <div class="shell" :class="{ 'shell--minimized': loading }">
    <div class="sidebar">
      <nav
        @click="toggleServicesNav()"
        class="services-nav"
        v-bind:class="{ active: servicesNavState }"
        v-show="!loading"
      >
        <div class="row no-gutters">
          <div
            :class="{ 'ml-text-hidden': loading }"
            class="col d-flex align-items-center justify-content-between services-nav__services-dropdown"
          >
            <span id="service-name-display">{{
              currentService.serviceName
            }}</span>
            <i class="material-icons">expand_more</i>
          </div>
        </div>
        <div class="row no-gutters">
          <div
            :class="[
              'col',
              'mt-1',
              'services-nav__service-code',
              { 'ml-text-hidden': loading },
            ]"
          >
            <span>CODE: {{ currentService.serviceCode }}</span>
          </div>
        </div>

        <div class="active-show">
          <!-- Other services list -->
          <ul class="services-nav__nav-list">
            <li class="list-title">Your services</li>
            <li
              v-for="service in allServices"
              @click="pickService(service)"
              :key="`#${service.serviceName}`"
            >
              {{ service.serviceName }}
            </li>
          </ul>

          <!-- New service -->
          <ul class="services-nav__nav-list">
            <li @click="createService">
              <i class="material-icons">add</i>
              <span>New Service</span>
            </li>
          </ul>

          <!-- Reset Password -->
          <ul class="services-nav__nav-list">
            <li @click="triggerModal">
              <i class="material-icons">security</i>
              <span>Reset Password</span>
            </li>
          </ul>

          <!-- Log out -->
          <ul class="services-nav__nav-list">
            <li @click="logoutUser">
              <i class="material-icons">exit_to_app</i>
              <span>Logout</span>
            </li>
          </ul>
        </div>
      </nav>
      <ul class="sidenav-list-item" v-show="!loading">
        <li @click="goTo('/home')" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons-round">home</i>
            <span>Home</span>
          </div>
        </li>

        <li @click="openTinggDocumentation()" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons-outlined">map</i>
            <span>Documentation</span>
          </div>
        </li>

        <li class="sidenav-list-item__title">Analytics</li>

        <li @click="goTo('/visits')" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons">timeline</i>
            <span>Customer visits</span>
          </div>
        </li>

        <li @click="goTo('/payments')" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons">receipt</i>
            <span>Customer payments</span>
          </div>
        </li>

        <li class="sidenav-list-item__title">Configurations</li>

        <li @click="goTo('/payment/options')" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons-outlined">account_balance_wallet</i>
            <span>Payment options</span>
          </div>
        </li>

        <li @click="goTo('/users')" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons-outlined">people</i>
            <span>Users</span>
          </div>
        </li>

        <li @click="goTo('/profile')" class="sidenav-list-item__item">
          <div :class="{ 'ml-text-blurred': loading }">
            <i class="material-icons-outlined">account_circle</i>
            <span>Profile</span>
          </div>
        </li>
      </ul>
    </div>

    <div class="shell-content">
      <div class="row no-gutters ml-offline__banner">
        <div class="align-items-center col d-flex justify-content-center">
          <div class="ml-offline__banner-text">
            You are offline. Please check your internet connection.
          </div>
        </div>
      </div>
      <div class="row no-gutters">
        <div class="col">
          <div class="ml-breadcrumb">
            <span
              style="display: flex; align-items: center;"
              id="breadcrumbs"
            ></span>
          </div>
        </div>
      </div>
      <!-- This is where the actual page information will be rendered -->
      <slot></slot>
    </div>

    <modal
      action="Proceed"
      :no-icon="false"
      :name="`reset-password`"
      :title="`<strong>Reset Password?</strong>`"
      @cancel="cancelPasswordReset"
      @save="requestPasswordReset"
    >
      <div style="text-align: center;" class="form-group">
        <label class="control-label" for="user_email"
          >Click proceed to reset your password and check your email address
          below.</label
        >
        <input
          style="text-align: center;"
          type="email"
          v-model="userInfo.emailAddress"
          class="form-control"
          name="user_email"
          id="user_email"
          readonly
        />
      </div>
    </modal>
  </div>
</template>

<script>
import Vue from "vue/dist/vue";
import { setTimeout } from "timers";
import Modal from "@/components/Modal";
import Auth from "@/api/services/auth.js";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { SET_CURRENT_SERVICE } from "@/store/modules/services";

const logInfo = !!(process.env.NODE_ENV !== "production");

export default {
  name: "Shell",

  props: {
    breadcrumbs: {
      type: String,
    },
    loading: {
      default: false,
      type: Boolean,
    },
  },

  components: {
    modal: Modal,
  },

  computed: {
    ...mapGetters("services", ["currentService", "allServices"]),
    ...mapGetters("user", ["userInfo"]),
  },

  data() {
    return {
      actions: [
        {
          name: "Reset",
          event: "reset-password",
        },
      ],
      servicesNavState: false,
    };
  },

  methods: {
    ...mapActions("auth", ["logout"]),
    ...mapMutations("services", [SET_CURRENT_SERVICE]),

    buildBreadcrumbs() {
      let res = Vue.compile(
        `<span style="font-size: 14px; display: flex; align-items: center;">${this.breadcrumbs.replace(
          new RegExp(",", "gi"),
          "&nbsp;&nbsp;/&nbsp;&nbsp;"
        )}</span>`
      );

      new Vue({
        render: res.render,
        staticRenderFns: res.staticRenderFns,
      }).$mount("#breadcrumbs");
    },

    createService() {
      this.$router.push("/create/service");
    },

    triggerModal() {
      this.$modal.show("reset-password");
    },

    requestPasswordReset() {
      Auth.requestPasswordReset({ email: this.userInfo.emailAddress })
        .then((response) => {
          logInfo &&
            console.log(
              "The request to reset the password was successful",
              response
            );

          this.$snackbar.open({
            message: "A reset password email has been successfully sent",
            type: "is-success",
            position: "is-bottom",
            queue: false,
          });

          this.$modal.hide("reset-password");

          setTimeout(this.logoutUser(), 3000);
        })
        .catch((response) => {
          logInfo && console.log("The reset password request failed", response);
          this.showError = true;
          this.$btn.enable(".mula-btn");
        });
    },

    cancelPasswordReset() {
      this.$modal.hide("reset-password");
    },

    toggleServicesNav() {
      this.servicesNavState = !this.servicesNavState;
    },

    logoutUser() {
      this.logout();
      this.$router.push("/login");
    },

    pickService(service) {
      this[SET_CURRENT_SERVICE](service);
      window.location.reload(true);
    },

    openTinggDocumentation: function () {
      window.open("https://tingg.gitbook.io/checkout/", "_blank");
    },
  },

  mounted() {
    this.buildBreadcrumbs();
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/theme";

div.sidebar {
  background-image: linear-gradient(135deg, $tingg-primary-red, $tingg-purple);
}

.ml-breadcrumb {
  color: $tingg-purple;
}

nav.services-nav {
  border-radius: 2px;
  color: $tingg-dark;
  background-color: $tingg-white;
}

#service-name-display {
  height: 24px;
  text-align: left;
  overflow: hidden;
  line-height: 24px;
}

.sidenav-list-item__item {
  > div {
    > i[class^="material-icons"] {
      font-size: 24px;
      margin-right: 8px;
    }

    > span {
      letter-spacing: 0.4px;
    }

    display: flex;
    align-items: center;

    &:hover {
      color: rgba(255, 255, 255, 0.6);
    }
  }
}

.services-nav__nav-list {
  > li {
    i[class^="material-icons"] {
      font-size: 24px;
      margin-right: 8px;
    }

    display: flex;
    align-items: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Shell.vue